import { useEffect, useState } from 'react';
import type { ICustomControlDiagnosticReportReadProps } from '../../interfaces/customControlDiagnosticReportReadProps';
import { getServiceLabel } from '../../../../../../common/customControls/getServiceLabel';
import { ProcessEntity } from '../../../../../../common/enums/processEntity';
import { ServiceType } from '../../../../../../common/enums/serviceType';
import { SomnologyOrganizationType } from '../../../../../../common/enums/somnologyOrganizationType';
import { getDisplayName } from '../../../../../../common/utils/getDisplayName';
import { translateKey } from '../../../../../translate';
import { DiagnosticReportReadButtons } from '../../../../elements/carePathButtons/DiagnosticReportReadButtons';
import { ProcessContainer } from '../../../episodes/common';
import { ProcessItemFooter } from '../../../episodes/ProcessItemFooter';
import { ProcessItemHeader } from '../../../episodes/ProcessItemHeader';
import { OsasAddendumAdd } from '../../utils/v1/addendum/OsasAddendumAdd';
import { OsasAddendumRead } from '../../utils/v1/addendum/OsasAddendumRead';
import { CustomControlCollapseBody } from '../../utils/v1/CustomControlCollapseBody';
import { anamnesisFieldsKsyosSleep, anamnesisFieldsNsi } from './anamnesisFields';
import { Osasc1V1DiagnosticReportReadCustomControlFields } from './Osasc1V1DiagnosticReportReadCustomControlFields';
import { Osasc1V1DiagnosticReportSummary } from './Osasc1V1DiagnosticReportSummary';

export function Osasc1V1DiagnosticReportRead(props: ICustomControlDiagnosticReportReadProps) {
    const [isFooterVisible, setIsFooterVisible] = useState(false);

    useEffect(() => {
        checkIsFooterVisible();
    }, [props.diagnosticReport.customControlData]);

    return (
        <ProcessContainer>
            <ProcessItemHeader
                date={props.diagnosticReport && props.diagnosticReport.dateSent}
                isFirstItem
                isFirstProcess={props.itemState.isFirstProcess}
                isItemOpen={props.itemState.isOpen}
                onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                organizationName={props.diagnosticReport.performerOrganization?.name}
                practitionerName={
                    props.diagnosticReport.performerPractitioner
                        ? getDisplayName(props.diagnosticReport.performerPractitioner)
                        : undefined
                }
                process={props.process}
                serviceType={ServiceType.consultation}
                title={getServiceLabel(props.process.code, 'request', props.process.name)}
            />

            <Osasc1V1DiagnosticReportSummary
                customControlData={props.diagnosticReport.customControlData}
                isOpen={props.itemState.isOpen}
            />

            <CustomControlCollapseBody isOpen={props.itemState.isOpen}>
                <Osasc1V1DiagnosticReportReadCustomControlFields
                    customControlData={props.diagnosticReport.customControlData}
                    diagnosticReport={props.diagnosticReport}
                />
            </CustomControlCollapseBody>

            {isFooterVisible && (
                <ProcessItemFooter
                    isItemOpen={props.itemState.isOpen}
                    labelLess={translateKey('diagnosticReport.hideExaminationReport')}
                    labelMore={translateKey('diagnosticReport.showExaminationReport')}
                    onToggle={async (isOpened) => await props.updateItemState({ ...props.itemState, isOpen: isOpened })}
                />
            )}

            <OsasAddendumRead addendum={props.diagnosticReport.customControlData?.addendum} />

            <OsasAddendumAdd
                diagnosticReport={props.diagnosticReport}
                processEntity={ProcessEntity.diagnosticReport}
                reloadProcess={props.reloadProcess}
                request={props.request}
            />

            <DiagnosticReportReadButtons {...props} />
        </ProcessContainer>
    );

    function checkIsFooterVisible() {
        const anamnesisFields =
            props.diagnosticReport.customControlData.somnologyOrganizationType ===
            SomnologyOrganizationType.ksyosMedicalCenter
                ? anamnesisFieldsKsyosSleep
                : anamnesisFieldsNsi;

        for (const field of anamnesisFields) {
            if (
                props.diagnosticReport.customControlData[`${field.key}_value`] &&
                props.diagnosticReport.customControlData[`${field.key}_value`] !== 'Ja'
            ) {
                setIsFooterVisible(true);

                return;
            }
        }

        if (
            props.diagnosticReport.customControlData.tonsilSize ||
            props.diagnosticReport.customControlData.tongueSize ||
            props.diagnosticReport.customControlData.retrognathism ||
            props.diagnosticReport.customControlData.poligraphyExam ||
            props.diagnosticReport.remark
        ) {
            setIsFooterVisible(true);
        }
    }
}
